import { isPlatformServer } from '@angular/common';
import { WindowRef } from 'ngx-tag-commander';
import { TranslateService } from '@ngx-translate/core';
import { catchError, filter, map, startWith, switchMap, withLatestFrom } from 'rxjs/operators';
import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';
import {
  BasePageMetaResolver,
  CanonicalPageResolver,
  CanonicalUrlOptions,
  CmsService,
  PageDescriptionResolver,
  PageRobotsMeta,
  RoutingPageMetaResolver,
  RoutingService,
  TranslationService
} from '@spartacus/core';
import { combineLatest, Observable, of } from 'rxjs';
import { Request } from 'express';
import { NavigationEnd, Router } from '@angular/router';
import { DcpListResponse, RecipeSearchItem } from '../models';
import { BaseStoreService, CustomPageLinkService, RecipeService } from '../services';
import { SERVER_REQUEST } from '../constants';

@Injectable({
  providedIn: 'root'
})
export class CustomBasePageMetaResolver
  extends BasePageMetaResolver
  implements PageDescriptionResolver, CanonicalPageResolver
{
  private label$: Observable<string>;

  constructor(
    protected cmsService: CmsService,
    protected translation: TranslationService,
    protected routingPageMetaResolver: RoutingPageMetaResolver,
    protected routingService: RoutingService,
    private baseStoreService: BaseStoreService,
    private translateService: TranslateService,
    protected router: Router,
    protected pageLinkService: CustomPageLinkService,
    protected recipeService: RecipeService,
    @Inject(PLATFORM_ID) private platformId: any,
    @Optional() @Inject(SERVER_REQUEST) private request: Request,
    @Optional() private windowRef: WindowRef
  ) {
    super(cmsService, translation, routingPageMetaResolver, router, pageLinkService);
    this.title$ = this.cmsService.getCurrentPage().pipe(
      filter((p) => !!p),
      map((p) => p['titleTag'] || p.title)
    );
    this.description$ = this.cmsService.getCurrentPage().pipe(
      filter((p) => !!p),
      map((p) => p['descriptionTag'])
    );
    this.label$ = this.page$.pipe(map((page) => page.label));
    this.robots$ = this.page$.pipe(map((page) => page.robots || []));
  }

  resolveTitle(): Observable<string> {
    return combineLatest(
      [
        this.title$,
        this.baseStoreService.getBaseStore(),
        this.routingService.getRouterState(),
        this.page$
      ]
    ).pipe(
      switchMap(([title, baseStore, route, page]) => {
        if (page.template === 'RecipeCategoryPageTemplate') {
          return this.recipeService
            .search('', 0, 12, {
              'filters': [
                {
                  'field': 'domain.key',
                  'values': [
                    page['externalRecipeCategoryFilter']
                  ]
                }
              ]
            })
            .pipe(
              catchError(() => {
                this.router.navigate(['/not-found']);
                return of(undefined);
              }),
              switchMap((recipes: DcpListResponse<RecipeSearchItem>) => {
                return combineLatest([of(title), of(baseStore), of(route), of(recipes)]);
              })
            );
        } else {
          return combineLatest([of(title), of(baseStore), of(route), of(null)]);
        }
      }),
      map(([title, baseStore, route, recipeList]) => {
        const routeState = route.state.url.split('/');
        const product =
          routeState.indexOf('csc') > 3
            ? decodeURIComponent(routeState[routeState.indexOf('csc') - 1])
            : this.translateService.instant('product.products');
        let serviceTitle;
        if (route.state.context.id === '/faq/csc/products') {
          serviceTitle = `${this.translateService.instant('SEO.faqTitle')} ${product} ${baseStore.brandSite} `;
        } else {
          serviceTitle = undefined;
        }
        title = title?.replace(/<name>/gi, `${product}`);
        title = title?.replace(
          /<siteBrand>/gi,
          `${baseStore.brandSite?.charAt(0).toUpperCase()}${baseStore.brandSite?.toLowerCase().slice(1)}`
        );
        if (route.state.url.includes(`/crp/${route.state.context.id}`)) {
          const recipesSize = recipeList?.page?.totalElements;
          title = title.replace('{recipes_size}', recipesSize.toString());
        }
        if (route.state.queryParams?.page) {
          const page = parseInt(route.state.queryParams.page);
          if (page > 1) {
            title = title?.replace(
              /<page>/gi,
              `${this.translateService.instant('SEO.page')} ${page}`
            );
          } else {
            title = title?.replace(/<page>/gi, '');
          }
        } else {
          title = title?.replace(/<page>/gi, '');
        }
        return serviceTitle ? serviceTitle.replaceAll('  ', ' ') : title.replaceAll('  ', ' ');
      })
    );
  }

  resolveDescription(): Observable<string> {
    return this.description$.pipe(
      withLatestFrom(this.baseStoreService.getBaseStore(), this.routingService.getRouterState()),
      map(([description, baseStore, route]) => {
        if (route.state.queryParams?.page && parseInt(route.state.queryParams?.page) > 1) {
          return null;
        } else {
          const host = isPlatformServer(this.platformId)
            ? this.request.hostname
            : this.windowRef.nativeWindow.location.hostname;
          let serviceDescription;
          const routeState = route.state.url.split('/');
          const product =
            routeState.indexOf('csc') > 3
              ? decodeURIComponent(routeState[routeState.indexOf('csc') - 1])
              : this.translateService.instant('product.products');

          if (route.state.context.id === '/faq/csc/products') {
            serviceDescription = `${this.translateService.instant('SEO.faqDescription')} ${product} ${
              baseStore.brandSite
            } ${this.translateService.instant('SEO.online')} ${host}`;
          } else if (route.state.context.id === '/instructions-for-use') {
            serviceDescription = `${this.translateService.instant('SEO.noticeDescription')} ${product} ${
              baseStore.brandSite
            } ${this.translateService.instant('SEO.online')} ${host}`;
          } else {
            serviceDescription = undefined;
            description = description?.replace(
              /<siteBrand>/gi,
              `${baseStore.brandSite?.charAt(0).toUpperCase()}${baseStore.brandSite?.toLowerCase().slice(1)}`
            );
            if (route.state.url.includes(`/crp/${route.state.context.id}`)) {
              description = description?.slice(0, 155);
            }
          }
          return serviceDescription ? serviceDescription : description ? description : ' ';
        }
      })
    );
  }

  resolveCanonicalUrl(options?: CanonicalUrlOptions): Observable<string> {
    const events$ = this.router.events.pipe(
      filter((ev) => ev instanceof NavigationEnd),
      startWith(null)
    );
    return combineLatest(
      [
        events$,
        this.label$
      ]
    ).pipe(
      switchMap(([, label]) => this.pageLinkService.getTranslatedCanonicalUrl(options, label))
    );
  }

  resolveRobots(): Observable<PageRobotsMeta[]> {
    return this.routingService.getRouterState().pipe(
      map((route) => {
        return (route.state.url.includes('cart') || route.state.url.includes('checkout') || route.state.url.includes('order-confirmation'))
          ? [PageRobotsMeta.NOFOLLOW, PageRobotsMeta.NOINDEX]
          : [];
      })
    );
  }
}
